import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import PassInfoDialog from "@/components/passes/dialogs/PassInfoDialog";
import { mixpanelEvent } from "@/lib/mixpanels/mixpanel";
import { MYPAGE_SUBSCRIPTION } from "@/settings/constant";
import { TabContent, Tabs, Typography } from "@bookips/sds";
import { useRouter } from "next/router";
import { useCallback, useEffect, useState } from "react";
import Pagination from "../common/Pagination";
import useMyPageHome from "../hooks/useMyPageHome";
import PassActionButtons from "./PassActionButtons";
import PassList from "./PassList";
import { EXPIRED_PAGE_SIZE } from "./constants";
import { useMyPassesQuery } from "./hooks/useMyPassesQuery";
import { isBadgeableTicket } from "./types";
const MyPassesPage = ()=>{
    var _homeData_subscriptions;
    const router = useRouter();
    const [tab, setTab] = useState("active");
    const [expiredPage, setExpiredPage] = useState(1);
    const [selectedPass, setSelectedPass] = useState(null);
    const handleOpenInfo = useCallback((pass)=>{
        setSelectedPass(pass);
    }, []);
    const { data , isLoading  } = useMyPassesQuery();
    // 구독 보유 여부. MyPageLayout 이 이미 조회한 응답이라 같은 캐시를 읽는다 (추가 요청 없음)
    const { data: homeData  } = useMyPageHome();
    var _data_unexpired;
    // 서버가 이미 { unexpired, expired } 로 분리해서 내려줌.
    // 구독/쏠북플러스 등 서비스 외 카테고리가 섞여 올 수 있으므로 방어 필터 적용.
    const activeItems = ((_data_unexpired = data === null || data === void 0 ? void 0 : data.unexpired) !== null && _data_unexpired !== void 0 ? _data_unexpired : []).filter(isBadgeableTicket);
    var _data_expired;
    const expiredAll = ((_data_expired = data === null || data === void 0 ? void 0 : data.expired) !== null && _data_expired !== void 0 ? _data_expired : []).filter(isBadgeableTicket);
    const expiredPaged = expiredAll.slice((expiredPage - 1) * EXPIRED_PAGE_SIZE, expiredPage * EXPIRED_PAGE_SIZE);
    var _homeData_subscriptions_totalCount;
    /**
   * 사용중 이용권이 없고 구독을 보유했으면 구독 관리로 보낸다.
   *
   * 이 화면으로 오는 '이용권' 진입점이 GNB 드롭다운·마이쏠북 홈·주문 내역 등 열 곳이 넘어
   * 링크마다 분기할 수 없다. 목적지를 바꾸는 대신 도착한 화면에서 한 번에 받는다.
   *
   * 만료 이용권만 남은 경우도 대상이다 — 사용중 이용권이 없으면 이 화면에서 할 수 있는 게 없다.
   * 조회 전(isLoading)에는 판단하지 않는다. 빈 목록과 구분되지 않아 오이동이 난다.
   */ const hasSubscription = ((_homeData_subscriptions_totalCount = homeData === null || homeData === void 0 ? void 0 : (_homeData_subscriptions = homeData.subscriptions) === null || _homeData_subscriptions === void 0 ? void 0 : _homeData_subscriptions.totalCount) !== null && _homeData_subscriptions_totalCount !== void 0 ? _homeData_subscriptions_totalCount : 0) > 0;
    const shouldRedirectToSubscription = !isLoading && activeItems.length === 0 && hasSubscription;
    useEffect(()=>{
        if (!shouldRedirectToSubscription) return;
        // push 가 아니라 replace — 뒤로가기가 다시 이 화면으로 들어와 리다이렉트를 반복하지 않게 한다
        router.replace(MYPAGE_SUBSCRIPTION);
    }, [
        shouldRedirectToSubscription,
        router
    ]);
    // 사용/기간만료 탭으로 전환할 때 항상 1페이지부터 시작
    const handleTabChange = (next)=>{
        const nextTab = next;
        setTab(nextTab);
        if (nextTab === "expired") setExpiredPage(1);
        // MX-619 / MX-620 — 탭 클릭 시 Tab UI Clicked 발사
        mixpanelEvent("Tab UI Clicked", {
            Position: "이용권 관리",
            "Tab Type": "이용권 관리",
            "Tab Name": nextTab === "active" ? "사용중" : "사용/기간만료"
        });
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "bg-white sm:mt-8 sm:rounded-lg lg:mt-0",
        children: [
            /*#__PURE__*/ _jsxs(Tabs, {
                fullWidth: true,
                value: tab,
                onValueChange: handleTabChange,
                tabs: [
                    {
                        value: "active",
                        label: /*#__PURE__*/ _jsxs("span", {
                            className: "flex items-center gap-1",
                            children: [
                                "사용중",
                                /*#__PURE__*/ _jsx(Typography, {
                                    as: "span",
                                    variant: "subtitle1",
                                    className: "text-secondary-main",
                                    children: activeItems.length
                                })
                            ]
                        })
                    },
                    {
                        value: "expired",
                        label: "사용/기간만료"
                    }
                ],
                children: [
                    /*#__PURE__*/ _jsx(TabContent, {
                        value: "active"
                    }),
                    /*#__PURE__*/ _jsx(TabContent, {
                        value: "expired"
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(PassActionButtons, {}),
            /*#__PURE__*/ _jsx(PassList, {
                items: tab === "active" ? activeItems : expiredPaged,
                variant: tab,
                // 구독 관리로 떠나는 중에는 '이용권이 없어요' 를 띄우지 않는다 —
                // 구독을 보유한 유저에게 틀린 안내이고, 어차피 곧 사라질 화면이다
                isLoading: isLoading || shouldRedirectToSubscription,
                onOpenInfo: handleOpenInfo
            }),
            tab === "expired" && /*#__PURE__*/ _jsx(Pagination, {
                page: expiredPage,
                totalCount: expiredAll.length,
                pageSize: EXPIRED_PAGE_SIZE,
                onChange: setExpiredPage
            }),
            selectedPass && /*#__PURE__*/ _jsx(PassInfoDialog, {
                isOpen: true,
                pass: selectedPass.productInfo,
                ticket: selectedPass,
                mode: "owned",
                position: "마이쏠북",
                onPrimary: ()=>setSelectedPass(null),
                onClose: ()=>setSelectedPass(null)
            })
        ]
    });
};
export default MyPassesPage;
